import React, { useState, useRef, useEffect } from 'react';

type CardMC2PanelProps = {
  leftPanel: React.ReactNode;
  rightPanel: React.ReactNode;
  initialLeftWidth?: number;
  minLeftWidth?: number;
  maxLeftWidth?: number;
}

const CardMC2Panel: React.FC<CardMC2PanelProps> = ({
  leftPanel,
  rightPanel,
  initialLeftWidth = 250,
  minLeftWidth = 200,
  maxLeftWidth = 500,
}) => {
  const [leftWidth, setLeftWidth] = useState(initialLeftWidth);
  const isDragging = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const startResize = (e: React.MouseEvent) => {
    isDragging.current = true;
    document.addEventListener('mousemove', resize);
    document.addEventListener('mouseup', stopResize);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none'; // Prevent text selection while resizing
  };

  const resize = (e: MouseEvent) => {
    if (!isDragging.current || !containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const newWidth = e.clientX - containerRect.left;

    if (newWidth >= minLeftWidth && newWidth <= maxLeftWidth) {
      setLeftWidth(newWidth);
    }
  };

  const stopResize = () => {
    isDragging.current = false;
    document.removeEventListener('mousemove', resize);
    document.removeEventListener('mouseup', stopResize);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', resize);
      document.removeEventListener('mouseup', stopResize);
    };
  }, []);

  return (
    <div
      id="card-mc-2-panel"
      ref={containerRef}
      style={{
        display: 'flex',
        height: '100%',
        minHeight: '600px',
        backgroundColor: '#fff',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        overflow: 'hidden', // Contain potential overflow
        marginTop: '-1rem'
      }}
    >
      {/* Left Panel */}
      <div
        style={{
          width: `${leftWidth}px`,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          borderRight: '1px solid #e0e0e0', // Visual border, though resize handle covers it
          position: 'relative',
          backgroundColor: '#fafafa'
        }}
      >
        {leftPanel}
      </div>

      {/* Resize Handle */}
      <div
        onMouseDown={startResize}
        style={{
          width: '5px',
          cursor: 'col-resize',
          backgroundColor: 'transparent',
          zIndex: 10,
          marginLeft: '-2px', // Center over the border
          marginRight: '-3px',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        className="resize-handle"
      >
        {/* Visual indicator for handle (optional, invisible by default for clean look but strictly functional) */}
        <div style={{ width: '1px', height: '100%', backgroundColor: '#e0e0e0' }}></div>
      </div>

      {/* Right Panel */}
      <div style={{ flex: 1, padding: '20px', overflowY: 'auto', backgroundColor: '#fff' }}>
        {rightPanel}
      </div>

    </div>
  );
};

export default CardMC2Panel;
